// Editor for the data-layer-driven fields on CompanyPayrollConfig that
// previously had no UI. Each section reads/writes one schema slice. The
// parent component owns the surrounding form/save flow; this component
// just emits `onChange(patch)` whenever the user edits a slice.

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Collapse,
  Table,
  Button,
  Space,
  Input,
  InputNumber,
  Select,
  Switch,
  Popconfirm,
  Tag,
  Modal,
  message,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
} from '@ant-design/icons';

const STATUS_OPTIONS = ['010', '020', '029', '030', '033', '040', '050', '060'];
const DOP_GRUPA_OPTIONS = ['D08', 'D16', 'DO2', 'DO5', 'PR1', 'N01'];
const POREZ_OPTIONS = ['P01', 'P04', 'P14'];

// Work types the engine consults workTypeOverrides for: overtime-family types
// (resolveOvertimeCodes) + absence types that default to TYPE_40 average and
// can be rerouted to fixed contract rate via obracun='02' (AB Soft "f" codes).
const WORK_TYPE_OVERRIDE_OPTIONS = [
  'overTime',
  'overTime2',
  'nightWork',
  'nightWorkRegularWork',
  'overtimeNight',
  'overtimeOnPublicHoliday',
  'overtimeNightPublicHoliday',
  'nightWorkPublicHoliday',
  'workPublicHoliday',
  'weekendWork',
  'holidayLeave',
  'publicHoliday',
  'paidLeave',
  'paidLeaveWedding',
  'paidLeaveSlava',
  'paidLeaveDeath',
  'paidLeaveOther',
  'sickLeave30_65',
  'sickLeave30_100',
  'sickLeave30Up_65',
  'sickLeave30Up_100',
  'workInjury',
];

// AB Soft per-firma tuning flags on companyConfig.absoft that previously had no
// UI (written only via seed scripts). Labels are Serbian-primary (tech-admin
// panel). Every flag the engine reads MUST also exist in absoftConfigSchema,
// otherwise a strict UI save would silently strip it.
const ABSOFT_BOOL_FLAGS = [
  { key: 'useTaxGroups', label: 'Tax-grupe (net→bruto po grupi)', hint: 'P3/Arena reverse model — neto→bruto koeficijent po doprinosnoj grupi.' },
  { key: 'fullDayPremiumWork', label: 'Pun-dan noćni/praznik rad', hint: '232/234/236 = pun-dan po multiplikatoru baze (2.1/1.26/2.36×) u D08 grupi, ne OT overlay (Proctorio).' },
  { key: 'noMergeIntoDominant', label: 'Ne stapaj u dominantno odsustvo', hint: 'Drži svaku šifru odvojeno (praznik 156 za vreme dugog bolovanja ostaje 156) — Proctorio.' },
  { key: 'foldMinuliInComponents', label: 'Minuli u radnu šifru (fold)', hint: 'AB fold-uje Izn_minB u kod 100 umesto plutajućeg total-a (CCECC/Arena/Proctorio).' },
  { key: 'applyMinuliForward', label: 'Minuli na forward putanji (procMR)', hint: 'Primeni profile.absoft.procMR (AB Proc_min) kao minuli % na kod 100 i bez useCorrection (forward GROSS_TO_NET). Samo na stvaran rad TYPE_02, ne na TYPE_40 proseke (AI Digital).' },
  { key: 'prorateNonTaxableByFund', label: 'Neoporezivi po plaćenom fondu', hint: 'Proratuj 34.221 po paidFund/168 umesto po tipu ugovora (Arena/UNLIMIT/Proctorio).' },
  { key: 'type40HoursAreActual', label: 'TYPE_40 sati = stvarni AB sati', hint: 'Ne skaliraj TYPE_40 prosek part-time faktorom (Arena/Proctorio).' },
  { key: 'suppressAllowancesOnLongAbsence', label: 'Bez naknada na dugom bolovanju', hint: 'Bez meal/regresa za pun mesec dugog bolovanja(142)/trudničkog(147) (Arena).' },
  { key: 'suppressMealOnNoWork', label: 'Bez toplog obroka bez odrađenih dana', hint: 'Topli obrok (101) traži odrađene dane — pun mesec bilo kog odsustva (kratko bolovanje 132 itd.) = bez meala, regres ostaje (Arena).' },
  { key: 'dopunaFromInput', label: 'Dopuna 213 samo iz uvoza', hint: 'Dopuna do ugovorene (213) se ne izračunava nego se uzima iz uvezenog iznosa po radniku/mesecu — u AB Softu je to ručni unos (Obracun=09). Nema unosa = nema dopune (P3).' },
  { key: 'suppressBonusOnEarlyPregnancy', label: 'Bez bonusa na ranoj porodiljskoj', hint: 'Bonus (107) se ne isplaćuje u mesecu dominantne rane porodiljske (228) — država nadoknađuje 100% osnovice. Druga faza (147) i trudničko (122) zadržavaju bonus (P3).' },
  { key: 'overtimeInTaxGroupPool', label: 'OT u doprinosnoj grupi', hint: 'Prekovremeni (231) ulazi u D08 grupu i deli njen jedinstveni neto→bruto koeficijent umesto zasebnog gross-up-a. Bez toga se neoporezivi iznos deli na manji neto pa sve ostale šifre ispadnu niže za isti faktor (CCECC/Arena).' },
  { key: 'overtimeFromRegularBase', label: 'OT/noćni iz redovne baze', hint: 'Satnica za prekovremeni (231) i noćni (234) rad se računa iz redovnog rada (ugovoreni bruto / kalendarski fond), ne iz ukupne bruto baze koju plaćeni proseci (129/130) razblažuju — AB konvencija (Paysend).' },
  { key: 'pregnancyAlways147', label: 'Trudničko uvek 147', hint: 'Trudničko ostaje kod 147 (TYPE_62) celo trudničko, nikad 228/122 (Arena).' },
  { key: 'type62FrozenRate', label: 'TYPE_62 satnica zamrznuta na početak', hint: 'AB satnicu trudničkog (147) izračuna jednom — iz 12 meseci koji se završavaju mesec PRE početka odsustva — pa je do kraja samo množi satima meseca. Traži pz62 na komponentama istorije (EXAMROOM).' },
  { key: 'maternityNonTaxable', label: 'Neoporezivi na porodiljskom', hint: 'Neoporezivi (34.221 × plaćeni sati / fond meseca) na employer-paid trudničko 122/147 u mesecu dominantnog porodiljskog (P3).' },
  { key: 'prorateNonTaxableByComponentHours', label: 'Neoporezivi po satima komponenti', hint: 'Proratuj 34.221 po satima na EMITOVANIM šiframa (sati koje poslodavac stvarno plaća) kroz fond meseca. Državno porodiljsko stoji u evidenciji i puni fond, pa žena pola meseca na trudničkom a pola na porodiljskom inače dobije ceo neoporezivi iznos. Ima prednost nad SatiR i plaćenim fondom (P3).' },
  { key: 'maternityKeepRegres', label: 'Regres na porodiljskom', hint: 'U mesecu dominantnog porodiljskog AB guši meal (101) ali plaća regres (102) — zadrži samo regres (P3).' },
  { key: 'maternityRegresNeedsPaidHours', label: 'Regres na porodiljskom traži plaćen sat', hint: 'Uz „Regres na porodiljskom": regres ostaje samo ako poslodavac tog meseca plaća bar jedan sat. Ceo mesec o trošku države = AB čoveka nema na platnom spisku uopšte (P3, šest porodilja u julu 2026). Ne dira one koji uz porodiljsko imaju i trudničko ili rad.' },
  { key: 'otherIncomePayoutDeduct', label: 'Other-income (206) kao odbitak', hint: 'Kod 206 = benefit-in-kind: u netu, oduzima se od Za-isplatu proporcionalno umesto da se dodaje (P3).' },
  { key: 'prorateNonTaxableBySatiR', label: 'Neoporezivi po AB SatiR', hint: 'Proratuj neoporezivi po ia43 SatiR (svi plaćeni sati −140 isplata, kapirano na fond) umesto paidFund — noćne smene (Paysend).' },
  { key: 'disablePartTimePhFallback', label: 'Bez part-time praznik fallback-a', hint: 'Part-time praznik ostaje svoja šifra umesto stapanja u redovan rad (PCCC/Arena).' },
  { key: 'correctionMinuliInComponents', label: 'Minuli u korekciji (cilj pun bruto)', hint: 'Korekcija cilja pun ia43 bruto, minuli folded u kod 100 (Paysend).' },
  { key: 'promoteSickOnPublicHoliday', label: 'Bolovanje na praznik → 156', hint: 'Bolovanje ≤30d na dan praznika → publicHoliday/156 (Paysend).' },
  { key: 'hours2PublicHolidayAsFixed', label: 'hours2 praznik kao fiksno', hint: 'hours2 workType2=publicHoliday → sintetski praznik dan umesto OT (Paysend smenski).' },
  { key: 'useType02FondDenominator', label: 'TYPE_02 fond delilac', hint: 'TYPE_02 denominator varijanta (PCCC Bug #10/#10.1; Paysend noćni).' },
  { key: 'goShiftCarriedOver', label: 'GO carried-over shift −1m', hint: 'GO(130) carried-over 12m prozor pomeren −1 mesec (P3).' },
  { key: 'sickLeaveAnchorOnset', label: 'Bolovanje osnovica frozen na početak', hint: 'Nastavljeno bolovanje (132/198/121…) koristi prosek prozor [M-13..M-2] (osnovica zamrznuta na mesec kad je bolovanje počelo, kao AB @GodMesBol) — Arena.' },
  { key: 'rdIncentiveEnabled', label: 'R&D olakšica (čl.21i)', hint: 'Split 100/101/102 → 199/200/201 za R&D deo (UNLIMIT).' },
  { key: 'forwardIa31Caps', label: 'Forward ia31 cap doprinosa', hint: 'Primeni ia31 max/min osnovicu na forward doprinose bez useCorrection (UNLIMIT).' },
  { key: 'obrTip40Fallback', label: 'Obr Tip40 fallback', hint: 'Fallback na TYPE_40 obračun.' },
];

function ContributionStatusOverridesTable({ value, onChange }) {
  const { t } = useTranslation(['forms']);
  // value is a plain object/Map: { '025': { dopGrupa, baseFraction, skipEmployeeUnemployment } }
  const rows = useMemo(() => {
    if (!value) return [];
    const entries = value instanceof Map ? [...value] : Object.entries(value);
    return entries.map(([status, v]) => ({ status, ...(v || {}) }));
  }, [value]);
  const [editingIdx, setEditingIdx] = useState(null);
  const [draft, setDraft] = useState({});
  const commit = (list) => {
    const obj = {};
    for (const r of list) {
      if (!r.status) continue;
      obj[r.status] = {
        ...(r.dopGrupa ? { dopGrupa: r.dopGrupa } : {}),
        ...(r.baseFraction != null ? { baseFraction: r.baseFraction } : {}),
        ...(r.skipEmployeeUnemployment ? { skipEmployeeUnemployment: true } : {}),
      };
    }
    onChange(obj);
  };
  const onAdd = () => {
    const next = [...rows, { status: '', dopGrupa: '', baseFraction: null, skipEmployeeUnemployment: false }];
    setEditingIdx(rows.length);
    setDraft(next[next.length - 1]);
  };
  const onSave = (idx) => {
    const next = [...rows];
    next[idx] = { ...next[idx], ...draft };
    commit(next);
    setEditingIdx(null);
  };
  return (
    <Card
      size="small"
      title="Per-status override doprinosa"
      extra={<Button size="small" icon={<PlusOutlined />} onClick={onAdd} />}
      className="mb-3"
    >
      <div className="mb-2 text-xs text-gray-500">
        AB status → doprinosna grupa / umanjenje osnovice (npr. 025 → D12 bez nezaposlenosti; 028 → D15 baseFraction 0.30 novonastanjeni).
      </div>
      <Table
        size="small"
        rowKey={(_, i) => i}
        dataSource={editingIdx != null && editingIdx === rows.length ? [...rows, draft] : rows}
        pagination={false}
        columns={[
          {
            title: t('status'),
            dataIndex: 'status',
            width: 90,
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Input size="small" value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })} />
              ) : (
                <Tag>{v}</Tag>
              ),
          },
          {
            title: t('ozContributionGroup'),
            dataIndex: 'dopGrupa',
            width: 110,
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Input size="small" placeholder="D12 / D15" value={draft.dopGrupa} onChange={(e) => setDraft({ ...draft, dopGrupa: e.target.value })} />
              ) : (
                v || '—'
              ),
          },
          {
            title: 'baseFraction',
            dataIndex: 'baseFraction',
            width: 110,
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <InputNumber size="small" step={0.01} min={0} max={1} placeholder="0.30" value={draft.baseFraction} onChange={(val) => setDraft({ ...draft, baseFraction: val })} style={{ width: 90 }} />
              ) : (
                v ?? '—'
              ),
          },
          {
            title: 'bez nezaposl.',
            dataIndex: 'skipEmployeeUnemployment',
            width: 100,
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Switch size="small" checked={!!draft.skipEmployeeUnemployment} onChange={(c) => setDraft({ ...draft, skipEmployeeUnemployment: c })} />
              ) : v ? (
                <Tag color="green">✓</Tag>
              ) : (
                '—'
              ),
          },
          {
            title: '',
            key: 'actions',
            width: 90,
            render: (_, rec, idx) =>
              editingIdx === idx ? (
                <Space size="small">
                  <Button size="small" type="primary" onClick={() => onSave(idx)}>{t('save')}</Button>
                  <Button size="small" onClick={() => setEditingIdx(null)}>{t('cancel')}</Button>
                </Space>
              ) : (
                <Space size="small">
                  <Button size="small" icon={<EditOutlined />} onClick={() => { setEditingIdx(idx); setDraft(rec); }} />
                  <Popconfirm title={t('confirmDelete')} onConfirm={() => commit(rows.filter((_, i) => i !== idx))}>
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              ),
          },
        ]}
      />
    </Card>
  );
}

function useAllowanceMethodOptions() {
  const { t } = useTranslation(['forms']);
  return useMemo(
    () => [
      { value: 'perDay', label: t('ozAllowanceMethodPerDay') },
      { value: 'perDayHoliday', label: t('ozAllowanceMethodPerDayHoliday') },
      { value: 'fixed', label: t('ozAllowanceMethodFixed') },
      { value: 'automatic', label: t('ozAllowanceMethodAutomatic') },
      { value: 'automaticFund', label: t('ozAllowanceMethodAutomaticFund') },
      { value: 'manual', label: t('ozAllowanceMethodManual') },
      { value: 'monthlyFee', label: t('ozAllowanceMethodMonthlyFee') },
      { value: 'minimumDays', label: t('ozAllowanceMethodMinimumDays') },
    ],
    [t]
  );
}

function useModeOptions() {
  const { t } = useTranslation(['forms']);
  return useMemo(
    () => ({
      meal: [
        { value: 'PERCENT_MIN_WAGE', label: t('ozMealModePercent') },
        { value: 'FIXED', label: t('ozMealModeFixed') },
        { value: 'FROM_SALARY', label: t('ozMealModeFromSalary') },
      ],
      regres: [
        { value: 'FRACTION_MIN_WAGE', label: t('ozRegresModeFraction') },
        { value: 'FIXED', label: t('ozRegresModeFixed') },
        { value: 'FROM_SALARY', label: t('ozRegresModeFromSalary') },
      ],
      proportional: [
        { value: 'WORK_DAYS', label: t('ozProportionalWorkDays') },
        { value: 'START_STOP_ONLY', label: t('ozProportionalStartStop') },
      ],
    }),
    [t]
  );
}

function VariationMappingTable({ title, items, onChange, hint, showItemized }) {
  const { t } = useTranslation(['forms']);
  const [editingIdx, setEditingIdx] = useState(null);
  const [draft, setDraft] = useState({});
  const onAdd = () => {
    onChange([...(items || []), { variationKey: '', earningCode: '', name: '' }]);
    setEditingIdx((items || []).length);
    setDraft({ variationKey: '', earningCode: '', name: '' });
  };
  const onSave = (idx) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...draft };
    onChange(next);
    setEditingIdx(null);
  };
  const onRemove = (idx) => {
    const next = items.filter((_, i) => i !== idx);
    onChange(next);
  };
  return (
    <Card
      size="small"
      title={title}
      extra={<Button size="small" icon={<PlusOutlined />} onClick={onAdd} />}
      className="mb-3"
    >
      {hint ? <div className="mb-2 text-xs text-gray-500">{hint}</div> : null}
      <Table
        size="small"
        rowKey={(_, i) => i}
        dataSource={items || []}
        pagination={false}
        columns={[
          {
            title: t('ozVariationKey'),
            dataIndex: 'variationKey',
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Input
                  size="small"
                  value={draft.variationKey}
                  onChange={(e) =>
                    setDraft({ ...draft, variationKey: e.target.value })
                  }
                />
              ) : (
                v
              ),
          },
          {
            title: t('earningCode'),
            dataIndex: 'earningCode',
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Input
                  size="small"
                  value={draft.earningCode}
                  onChange={(e) =>
                    setDraft({ ...draft, earningCode: e.target.value })
                  }
                />
              ) : (
                v
              ),
          },
          {
            title: t('name'),
            dataIndex: 'name',
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Input
                  size="small"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                />
              ) : (
                v
              ),
          },
          ...(showItemized
            ? [
                {
                  // Šifre sa kartice koje razuđuju ovaj mesečni unos. Unos je
                  // UKUPAN iznos, kartica su njegove stavke, a pod "Šifra
                  // zarade" ide samo ostatak. Prazno = staro ponašanje (gasi se
                  // samo identična šifra), pa se unos i kartica SABIRAJU.
                  title: t('ozVariationItemized'),
                  dataIndex: 'itemizedFromCardCodes',
                  width: 210,
                  render: (v, _, idx) =>
                    editingIdx === idx ? (
                      <Select
                        size="small"
                        mode="tags"
                        className="w-full"
                        placeholder="P3O, P3P, P30"
                        value={draft.itemizedFromCardCodes || []}
                        onChange={(codes) =>
                          setDraft({
                            ...draft,
                            itemizedFromCardCodes: codes.length ? codes : undefined,
                          })
                        }
                      />
                    ) : (
                      (v || []).join(', ')
                    ),
                },
              ]
            : []),
          {
            title: t('ozNonCash'),
            dataIndex: 'nonCash',
            width: 90,
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Switch
                  size="small"
                  checked={!!draft.nonCash}
                  onChange={(c) => setDraft({ ...draft, nonCash: c })}
                />
              ) : v ? (
                t('yes')
              ) : (
                ''
              ),
          },
          {
            title: '',
            key: 'actions',
            width: 90,
            render: (_, rec, idx) =>
              editingIdx === idx ? (
                <Space size="small">
                  <Button size="small" type="primary" onClick={() => onSave(idx)}>
                    {t('save')}
                  </Button>
                  <Button size="small" onClick={() => setEditingIdx(null)}>
                    {t('cancel')}
                  </Button>
                </Space>
              ) : (
                <Space size="small">
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => {
                      setEditingIdx(idx);
                      setDraft(rec);
                    }}
                  />
                  <Popconfirm
                    title={t('confirmDelete')}
                    onConfirm={() => onRemove(idx)}
                  >
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              ),
          },
        ]}
      />
    </Card>
  );
}

function StatusMatrixOverridesTable({ items, onChange }) {
  const { t } = useTranslation(['forms']);
  const [editingIdx, setEditingIdx] = useState(null);
  const [draft, setDraft] = useState({});
  const onAdd = () => {
    onChange([
      ...(items || []),
      { earningCode: '', dopGrupa: 'D08', porez: 'P01' },
    ]);
    setEditingIdx((items || []).length);
    setDraft({ earningCode: '', dopGrupa: 'D08', porez: 'P01' });
  };
  const onSave = (idx) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...draft };
    onChange(next);
    setEditingIdx(null);
  };
  return (
    <Card
      size="small"
      title={t('ozStatusMatrixTitle')}
      extra={<Button size="small" icon={<PlusOutlined />} onClick={onAdd} />}
      className="mb-3"
    >
      <div className="mb-2 text-xs text-gray-500">
        {t('ozStatusMatrixHint')}
      </div>
      <Table
        size="small"
        rowKey={(_, i) => i}
        dataSource={items || []}
        pagination={false}
        columns={[
          {
            title: t('earningCode'),
            dataIndex: 'earningCode',
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Input
                  size="small"
                  value={draft.earningCode}
                  onChange={(e) =>
                    setDraft({ ...draft, earningCode: e.target.value })
                  }
                />
              ) : (
                v
              ),
          },
          {
            title: t('status'),
            dataIndex: 'status',
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Select
                  size="small"
                  allowClear
                  style={{ width: 80 }}
                  value={draft.status}
                  options={STATUS_OPTIONS.map((s) => ({ value: s, label: s }))}
                  onChange={(s) => setDraft({ ...draft, status: s })}
                />
              ) : (
                v || t('ozAny')
              ),
          },
          {
            title: t('ozContributionGroup'),
            dataIndex: 'dopGrupa',
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Select
                  size="small"
                  style={{ width: 80 }}
                  value={draft.dopGrupa}
                  options={DOP_GRUPA_OPTIONS.map((o) => ({ value: o, label: o }))}
                  onChange={(s) => setDraft({ ...draft, dopGrupa: s })}
                />
              ) : (
                v
              ),
          },
          {
            title: t('tax'),
            dataIndex: 'porez',
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Select
                  size="small"
                  style={{ width: 70 }}
                  value={draft.porez}
                  options={POREZ_OPTIONS.map((o) => ({ value: o, label: o }))}
                  onChange={(s) => setDraft({ ...draft, porez: s })}
                />
              ) : (
                v
              ),
          },
          {
            title: t('ozReroute'),
            dataIndex: 'rerouteToEarningCode',
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Input
                  size="small"
                  placeholder={t('optional')}
                  value={draft.rerouteToEarningCode}
                  onChange={(e) =>
                    setDraft({ ...draft, rerouteToEarningCode: e.target.value })
                  }
                />
              ) : (
                v || '—'
              ),
          },
          {
            title: '',
            key: 'actions',
            width: 90,
            render: (_, rec, idx) =>
              editingIdx === idx ? (
                <Space size="small">
                  <Button size="small" type="primary" onClick={() => onSave(idx)}>
                    {t('save')}
                  </Button>
                  <Button size="small" onClick={() => setEditingIdx(null)}>
                    {t('cancel')}
                  </Button>
                </Space>
              ) : (
                <Space size="small">
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => {
                      setEditingIdx(idx);
                      setDraft(rec);
                    }}
                  />
                  <Popconfirm
                    title={t('confirmDelete')}
                    onConfirm={() => onChange(items.filter((_, i) => i !== idx))}
                  >
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              ),
          },
        ]}
      />
    </Card>
  );
}

function EarningCodeBucketsTable({ items, onChange }) {
  const { t } = useTranslation(['forms']);
  const [editingIdx, setEditingIdx] = useState(null);
  const [draft, setDraft] = useState({});
  const onAdd = () => {
    onChange([...(items || []), { code: '', dopGrupa: 'D08', porez: 'P01' }]);
    setEditingIdx((items || []).length);
    setDraft({ code: '', dopGrupa: 'D08', porez: 'P01' });
  };
  const onSave = (idx) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...draft };
    onChange(next);
    setEditingIdx(null);
  };
  return (
    <Card
      size="small"
      title={t('ozEarningBucketsTitle')}
      extra={<Button size="small" icon={<PlusOutlined />} onClick={onAdd} />}
      className="mb-3"
    >
      <div className="mb-2 text-xs text-gray-500">
        {t('ozEarningBucketsHint')}
      </div>
      <Table
        size="small"
        rowKey={(_, i) => i}
        dataSource={items || []}
        pagination={false}
        columns={[
          {
            title: t('earningCode'),
            dataIndex: 'code',
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Input
                  size="small"
                  value={draft.code}
                  onChange={(e) => setDraft({ ...draft, code: e.target.value })}
                />
              ) : (
                v
              ),
          },
          {
            title: t('ozContributionGroup'),
            dataIndex: 'dopGrupa',
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Select
                  size="small"
                  style={{ width: 80 }}
                  value={draft.dopGrupa}
                  options={DOP_GRUPA_OPTIONS.map((o) => ({ value: o, label: o }))}
                  onChange={(s) => setDraft({ ...draft, dopGrupa: s })}
                />
              ) : (
                v
              ),
          },
          {
            title: t('tax'),
            dataIndex: 'porez',
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Select
                  size="small"
                  style={{ width: 70 }}
                  value={draft.porez}
                  options={POREZ_OPTIONS.map((o) => ({ value: o, label: o }))}
                  onChange={(s) => setDraft({ ...draft, porez: s })}
                />
              ) : (
                v
              ),
          },
          {
            title: '',
            key: 'actions',
            width: 90,
            render: (_, rec, idx) =>
              editingIdx === idx ? (
                <Space size="small">
                  <Button size="small" type="primary" onClick={() => onSave(idx)}>
                    {t('save')}
                  </Button>
                  <Button size="small" onClick={() => setEditingIdx(null)}>
                    {t('cancel')}
                  </Button>
                </Space>
              ) : (
                <Space size="small">
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => {
                      setEditingIdx(idx);
                      setDraft(rec);
                    }}
                  />
                  <Popconfirm
                    title={t('confirmDelete')}
                    onConfirm={() => onChange(items.filter((_, i) => i !== idx))}
                  >
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              ),
          },
        ]}
      />
    </Card>
  );
}

function Ia31MonthEditor({ ia31, onChange }) {
  const { t } = useTranslation(['forms']);
  const entries = useMemo(() => {
    if (!ia31) return [];
    if (ia31 instanceof Map) return [...ia31].map(([k, v]) => ({ godMes: k, ...v }));
    return Object.entries(ia31).map(([k, v]) => ({ godMes: k, ...v }));
  }, [ia31]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [draft, setDraft] = useState('');
  return (
    <Card
      size="small"
      title={t('ozIa31Title')}
      extra={
        <Button
          size="small"
          icon={<EditOutlined />}
          onClick={() => {
            setDraft(JSON.stringify(ia31 || {}, null, 2));
            setEditorOpen(true);
          }}
        >
          {t('ozJsonEditor')}
        </Button>
      }
      className="mb-3"
    >
      <Table
        size="small"
        rowKey="godMes"
        dataSource={entries}
        pagination={false}
        columns={[
          { title: t('ozGodMes'), dataIndex: 'godMes', width: 100 },
          { title: t('ozIa31Hours'), dataIndex: 'sati' },
          { title: t('ozIa31PointValue'), dataIndex: 'bodIznos' },
          { title: t('ozIa31BodSati'), dataIndex: 'bodSati' },
          { title: t('ozIa31MaxBase'), dataIndex: 'maxOsnovica' },
          { title: t('ozIa31MaxNetBase'), dataIndex: 'maxNetoOsnovica' },
          { title: t('ozIa31MinBase'), dataIndex: 'minOsnovica' },
        ]}
      />
      <Modal
        open={editorOpen}
        onCancel={() => setEditorOpen(false)}
        onOk={() => {
          try {
            const parsed = JSON.parse(draft);
            onChange(parsed);
            setEditorOpen(false);
            message.success(t('saved'));
          } catch (err) {
            message.error(`${t('ozInvalidJson')}: ${err.message}`);
          }
        }}
        title={t('ozIa31JsonTitle')}
        width={720}
      >
        <Input.TextArea
          rows={18}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          style={{ fontFamily: 'monospace' }}
        />
      </Modal>
    </Card>
  );
}

function SectorRulesTable({ items, onChange }) {
  const { t } = useTranslation(['forms']);
  const modes = useModeOptions();
  const [editingIdx, setEditingIdx] = useState(null);
  const [draft, setDraft] = useState({});
  const onAdd = () => {
    const empty = {
      sectorCode: '',
      name: '',
      mealAllowanceMode: 'FROM_SALARY',
      mealAllowanceValue: null,
      regresMode: 'FROM_SALARY',
      regresValue: null,
      proportionalMode: 'WORK_DAYS',
    };
    onChange([...(items || []), empty]);
    setEditingIdx((items || []).length);
    setDraft(empty);
  };
  const onSave = (idx) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...draft };
    onChange(next);
    setEditingIdx(null);
  };
  return (
    <Card
      size="small"
      title={t('ozSectorRules')}
      extra={<Button size="small" icon={<PlusOutlined />} onClick={onAdd} />}
      className="mb-3"
    >
      <div className="mb-2 text-xs text-gray-500">{t('ozSectorRulesHint')}</div>
      <Table
        size="small"
        rowKey={(_, i) => i}
        dataSource={items || []}
        pagination={false}
        columns={[
          {
            title: t('ozSectorCode'),
            dataIndex: 'sectorCode',
            width: 90,
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Input
                  size="small"
                  value={draft.sectorCode}
                  onChange={(e) =>
                    setDraft({ ...draft, sectorCode: e.target.value })
                  }
                />
              ) : (
                <Tag>{v}</Tag>
              ),
          },
          {
            title: t('name'),
            dataIndex: 'name',
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Input
                  size="small"
                  value={draft.name}
                  onChange={(e) =>
                    setDraft({ ...draft, name: e.target.value })
                  }
                />
              ) : (
                v || ''
              ),
          },
          {
            title: t('ozMealMode'),
            dataIndex: 'mealAllowanceMode',
            width: 160,
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Select
                  size="small"
                  style={{ width: 150 }}
                  value={draft.mealAllowanceMode}
                  options={modes.meal}
                  onChange={(val) =>
                    setDraft({ ...draft, mealAllowanceMode: val })
                  }
                />
              ) : (
                modes.meal.find((o) => o.value === v)?.label || v
              ),
          },
          {
            title: t('ozMealValue'),
            dataIndex: 'mealAllowanceValue',
            width: 100,
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <InputNumber
                  size="small"
                  value={draft.mealAllowanceValue}
                  onChange={(val) =>
                    setDraft({ ...draft, mealAllowanceValue: val })
                  }
                  style={{ width: 90 }}
                />
              ) : (
                v ?? '-'
              ),
          },
          {
            title: t('ozRegresMode'),
            dataIndex: 'regresMode',
            width: 170,
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Select
                  size="small"
                  style={{ width: 160 }}
                  value={draft.regresMode}
                  options={modes.regres}
                  onChange={(val) => setDraft({ ...draft, regresMode: val })}
                />
              ) : (
                modes.regres.find((o) => o.value === v)?.label || v
              ),
          },
          {
            title: t('ozRegresValue'),
            dataIndex: 'regresValue',
            width: 100,
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <InputNumber
                  size="small"
                  value={draft.regresValue}
                  onChange={(val) => setDraft({ ...draft, regresValue: val })}
                  style={{ width: 90 }}
                />
              ) : (
                v ?? '-'
              ),
          },
          {
            title: t('ozProportionalMode'),
            dataIndex: 'proportionalMode',
            width: 140,
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Select
                  size="small"
                  style={{ width: 130 }}
                  value={draft.proportionalMode}
                  options={modes.proportional}
                  onChange={(val) =>
                    setDraft({ ...draft, proportionalMode: val })
                  }
                />
              ) : (
                modes.proportional.find((o) => o.value === v)?.label || v
              ),
          },
          {
            title: '',
            key: 'actions',
            width: 90,
            render: (_, rec, idx) =>
              editingIdx === idx ? (
                <Space size="small">
                  <Button size="small" type="primary" onClick={() => onSave(idx)}>
                    {t('save')}
                  </Button>
                  <Button size="small" onClick={() => setEditingIdx(null)}>
                    {t('cancel')}
                  </Button>
                </Space>
              ) : (
                <Space size="small">
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => {
                      setEditingIdx(idx);
                      setDraft(rec);
                    }}
                  />
                  <Popconfirm
                    title={t('confirmDelete')}
                    onConfirm={() => onChange(items.filter((_, i) => i !== idx))}
                  >
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              ),
          },
        ]}
      />
    </Card>
  );
}

function EarningCodeOverridesTable({ items, onChange }) {
  const { t } = useTranslation(['forms']);
  const [editingIdx, setEditingIdx] = useState(null);
  const [draft, setDraft] = useState({});
  const onAdd = () => {
    onChange([
      ...(items || []),
      { code: '', naziv: '', obracun: '', procIzn: 100 },
    ]);
    setEditingIdx((items || []).length);
    setDraft({ code: '', naziv: '', obracun: '', procIzn: 100 });
  };
  const onSave = (idx) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...draft };
    onChange(next);
    setEditingIdx(null);
  };
  return (
    <Card
      size="small"
      title={t('ozEarningCodeOverridesTitle')}
      extra={<Button size="small" icon={<PlusOutlined />} onClick={onAdd} />}
      className="mb-3"
    >
      <div className="mb-2 text-xs text-gray-500">
        {t('ozEarningCodeOverridesHint')}
      </div>
      <Table
        size="small"
        rowKey={(_, i) => i}
        dataSource={items || []}
        pagination={false}
        scroll={{ x: 1000 }}
        columns={[
          {
            title: t('code'),
            dataIndex: 'code',
            width: 80,
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Input
                  size="small"
                  value={draft.code}
                  onChange={(e) =>
                    setDraft({ ...draft, code: e.target.value })
                  }
                />
              ) : (
                v
              ),
          },
          {
            title: t('name'),
            dataIndex: 'naziv',
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Input
                  size="small"
                  value={draft.naziv}
                  onChange={(e) =>
                    setDraft({ ...draft, naziv: e.target.value })
                  }
                />
              ) : (
                v
              ),
          },
          {
            title: t('ozObracun'),
            dataIndex: 'obracun',
            width: 90,
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Input
                  size="small"
                  value={draft.obracun}
                  onChange={(e) =>
                    setDraft({ ...draft, obracun: e.target.value })
                  }
                />
              ) : (
                v
              ),
          },
          {
            title: t('ozProcIzn'),
            dataIndex: 'procIzn',
            width: 80,
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <InputNumber
                  size="small"
                  value={draft.procIzn}
                  onChange={(val) => setDraft({ ...draft, procIzn: val })}
                  style={{ width: 70 }}
                />
              ) : (
                v
              ),
          },
          {
            title: t('ozPz'),
            dataIndex: 'pz',
            width: 60,
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <InputNumber
                  size="small"
                  value={draft.pz}
                  onChange={(val) => setDraft({ ...draft, pz: val })}
                  style={{ width: 50 }}
                />
              ) : (
                v ?? '-'
              ),
          },
          {
            title: t('ozPz41'),
            dataIndex: 'pz41',
            width: 60,
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <InputNumber
                  size="small"
                  value={draft.pz41}
                  onChange={(val) => setDraft({ ...draft, pz41: val })}
                  style={{ width: 50 }}
                />
              ) : (
                v ?? '-'
              ),
          },
          {
            title: t('ozPz62'),
            dataIndex: 'pz62',
            width: 60,
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <InputNumber
                  size="small"
                  value={draft.pz62}
                  onChange={(val) => setDraft({ ...draft, pz62: val })}
                  style={{ width: 50 }}
                />
              ) : (
                v ?? '-'
              ),
          },
          {
            title: t('ozMinRad'),
            dataIndex: 'minRad',
            width: 70,
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Input
                  size="small"
                  value={draft.minRad}
                  onChange={(e) =>
                    setDraft({ ...draft, minRad: e.target.value })
                  }
                />
              ) : (
                v
              ),
          },
          {
            title: t('notes'),
            dataIndex: 'notes',
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Input
                  size="small"
                  value={draft.notes}
                  onChange={(e) =>
                    setDraft({ ...draft, notes: e.target.value })
                  }
                />
              ) : (
                v
              ),
          },
          {
            title: '',
            key: 'actions',
            width: 90,
            render: (_, rec, idx) =>
              editingIdx === idx ? (
                <Space size="small">
                  <Button size="small" type="primary" onClick={() => onSave(idx)}>
                    {t('save')}
                  </Button>
                  <Button size="small" onClick={() => setEditingIdx(null)}>
                    {t('cancel')}
                  </Button>
                </Space>
              ) : (
                <Space size="small">
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => {
                      setEditingIdx(idx);
                      setDraft(rec);
                    }}
                  />
                  <Popconfirm
                    title={t('confirmDelete')}
                    onConfirm={() => onChange(items.filter((_, i) => i !== idx))}
                  >
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              ),
          },
        ]}
      />
    </Card>
  );
}

function WorkTypeOverridesTable({ items, onChange }) {
  const { t } = useTranslation(['forms']);
  const [editingIdx, setEditingIdx] = useState(null);
  const [draft, setDraft] = useState({});
  const onAdd = () => {
    const blank = {
      workType: 'holidayLeave',
      earningCode: '',
      name: '',
      obracun: '02',
      procIzn: 0,
      effectiveFrom: null,
      effectiveTo: null,
      allowPerEmpPremium: false,
      allowSalaryManualAmount: false,
    };
    onChange([...(items || []), blank]);
    setEditingIdx((items || []).length);
    setDraft(blank);
  };
  const onSave = (idx) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...draft };
    onChange(next);
    setEditingIdx(null);
  };
  const dateStr = (v) => (v ? String(v).slice(0, 10) : '');
  return (
    <Card
      size="small"
      title={t('ozPanelWorkTypeOverrides')}
      extra={<Button size="small" icon={<PlusOutlined />} onClick={onAdd} />}
      className="mb-3"
    >
      <div className="mb-2 text-xs text-gray-500">
        {t('ozWorkTypeOverridesHint')}
      </div>
      <Table
        size="small"
        rowKey={(_, i) => i}
        dataSource={items || []}
        pagination={false}
        scroll={{ x: 1100 }}
        columns={[
          {
            title: t('workType'),
            dataIndex: 'workType',
            width: 190,
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Select
                  size="small"
                  className="w-full"
                  showSearch
                  value={draft.workType}
                  options={WORK_TYPE_OVERRIDE_OPTIONS.map((w) => ({
                    value: w,
                    label: w,
                  }))}
                  onChange={(val) => setDraft({ ...draft, workType: val })}
                />
              ) : (
                <Tag>{v}</Tag>
              ),
          },
          {
            title: t('code'),
            dataIndex: 'earningCode',
            width: 80,
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Input
                  size="small"
                  value={draft.earningCode}
                  onChange={(e) =>
                    setDraft({ ...draft, earningCode: e.target.value })
                  }
                />
              ) : (
                v
              ),
          },
          {
            title: t('name'),
            dataIndex: 'name',
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Input
                  size="small"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                />
              ) : (
                v
              ),
          },
          {
            title: t('ozObracun'),
            dataIndex: 'obracun',
            width: 90,
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Select
                  size="small"
                  className="w-full"
                  value={draft.obracun}
                  options={[
                    { value: '02', label: '02' },
                    { value: '05', label: '05' },
                    { value: '62', label: '62' },
                  ]}
                  onChange={(val) => setDraft({ ...draft, obracun: val })}
                />
              ) : (
                v
              ),
          },
          {
            title: t('ozProcIzn'),
            dataIndex: 'procIzn',
            width: 80,
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <InputNumber
                  size="small"
                  value={draft.procIzn}
                  onChange={(val) => setDraft({ ...draft, procIzn: val })}
                  style={{ width: 70 }}
                />
              ) : (
                v ?? '-'
              ),
          },
          {
            title: t('effectiveFrom'),
            dataIndex: 'effectiveFrom',
            width: 110,
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Input
                  size="small"
                  placeholder="YYYY-MM-DD"
                  value={dateStr(draft.effectiveFrom)}
                  onChange={(e) =>
                    setDraft({ ...draft, effectiveFrom: e.target.value || null })
                  }
                />
              ) : (
                dateStr(v) || '-'
              ),
          },
          {
            title: t('ozEffectiveTo'),
            dataIndex: 'effectiveTo',
            width: 110,
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Input
                  size="small"
                  placeholder="YYYY-MM-DD"
                  value={dateStr(draft.effectiveTo)}
                  onChange={(e) =>
                    setDraft({ ...draft, effectiveTo: e.target.value || null })
                  }
                />
              ) : (
                dateStr(v) || '-'
              ),
          },
          {
            title: t('ozWtoAllowPremium'),
            dataIndex: 'allowPerEmpPremium',
            width: 90,
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Switch
                  size="small"
                  checked={!!draft.allowPerEmpPremium}
                  onChange={(val) =>
                    setDraft({ ...draft, allowPerEmpPremium: val })
                  }
                />
              ) : v ? (
                <Tag color="green">✓</Tag>
              ) : (
                '-'
              ),
          },
          {
            title: t('ozWtoAllowManualAmount'),
            dataIndex: 'allowSalaryManualAmount',
            width: 90,
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Switch
                  size="small"
                  checked={!!draft.allowSalaryManualAmount}
                  onChange={(val) =>
                    setDraft({ ...draft, allowSalaryManualAmount: val })
                  }
                />
              ) : v ? (
                <Tag color="green">✓</Tag>
              ) : (
                '-'
              ),
          },
          {
            title: t('notes'),
            dataIndex: 'notes',
            render: (v, _, idx) =>
              editingIdx === idx ? (
                <Input
                  size="small"
                  value={draft.notes}
                  onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                />
              ) : (
                v
              ),
          },
          {
            title: '',
            key: 'actions',
            width: 90,
            render: (_, rec, idx) =>
              editingIdx === idx ? (
                <Space size="small">
                  <Button size="small" type="primary" onClick={() => onSave(idx)}>
                    {t('save')}
                  </Button>
                  <Button size="small" onClick={() => setEditingIdx(null)}>
                    {t('cancel')}
                  </Button>
                </Space>
              ) : (
                <Space size="small">
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => {
                      setEditingIdx(idx);
                      setDraft(rec);
                    }}
                  />
                  <Popconfirm
                    title={t('confirmDelete')}
                    onConfirm={() => onChange(items.filter((_, i) => i !== idx))}
                  >
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              ),
          },
        ]}
      />
    </Card>
  );
}

export default function CompanyDataLayerConfig({ config, onChange }) {
  const { t } = useTranslation(['forms']);
  const allowanceMethodOptions = useAllowanceMethodOptions();
  // Sub-handlers create patch objects merged into parent state.
  const patch = (k, v) => onChange({ [k]: v });
  const patchNested = (k, sub, v) =>
    onChange({ [k]: { ...(config?.[k] || {}), [sub]: v } });
  const patchVar = (kind, list) =>
    onChange({
      variationMappings: { ...(config?.variationMappings || {}), [kind]: list },
    });
  const patchAbsoft = (sub, v) =>
    onChange({ absoft: { ...(config?.absoft || {}), [sub]: v } });
  // Patch a nested field on an absoft sub-object (e.g. absoft.topLevelBonus.code).
  const patchAbsoftNested = (sub, key, v) =>
    patchAbsoft(sub, { ...(config?.absoft?.[sub] || {}), [key]: v });
  const patchBugCompat = (sub, v) =>
    patchAbsoft('bugCompatibility', {
      ...(config?.absoft?.bugCompatibility || {}),
      [sub]: v,
    });
  const dopuna = config?.dopunaConfig || {};
  const topLevelNmb = config?.topLevelNmb || {};
  const topLevelBonus = config?.absoft?.topLevelBonus || {};
  const topLevelOtherIncome = config?.absoft?.topLevelOtherIncome || {};
  const nmbVoucherReroute = config?.absoft?.nmbVoucherReroute || {};
  const methods = config?.allowanceMethods || {};
  return (
    <Collapse className="mb-3" items={[
      {
        key: 'allowances',
        label: t('ozPanelAllowanceMethods'),
        children: (
          <div className="grid grid-cols-2 gap-3">
            {[
              ['mealAllowance', t('ozAllowanceMeal')],
              ['holidayAllowance', t('ozAllowanceRegres')],
              ['workFromHomeAllowance', t('ozAllowanceWfh')],
              ['travelAllowance', t('ozAllowanceTravel')],
            ].map(([key, label]) => (
              <div key={key}>
                <label className="block text-xs text-gray-500">{label}</label>
                <Select
                  allowClear
                  className="w-full"
                  value={methods[key]}
                  options={allowanceMethodOptions}
                  onChange={(v) =>
                    onChange({
                      allowanceMethods: { ...methods, [key]: v },
                    })
                  }
                />
              </div>
            ))}
          </div>
        ),
      },
      {
        key: 'topLevelNmb',
        label: t('ozPanelTopLevelNmb'),
        children: (
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-gray-500">{t('code')}</label>
              <Input
                value={topLevelNmb.code || ''}
                onChange={(e) => patchNested('topLevelNmb', 'code', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500">{t('name')}</label>
              <Input
                value={topLevelNmb.name || ''}
                onChange={(e) => patchNested('topLevelNmb', 'name', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500">
                {t('ozTopLevelNmbAlwaysApply')}
              </label>
              <Switch
                checked={!!topLevelNmb.alwaysApply}
                onChange={(v) => patchNested('topLevelNmb', 'alwaysApply', v)}
              />
            </div>
          </div>
        ),
      },
      {
        key: 'absoftTopLevelCodes',
        label: t('ozAbsoftTopLevelCodes'),
        children: (
          <div className="space-y-4">
            {/* absoft.topLevelBonus — salary.bonus kod override (STADION 128); alwaysApply emituje bonus i kad postoje bonus varijacije (Paysend 107 + flexi). */}
            <div>
              <div className="text-xs font-semibold text-gray-600 mb-1">{t('ozTopLevelBonusTitle')}</div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-gray-500">{t('code')}</label>
                  <Input
                    value={topLevelBonus.code || ''}
                    onChange={(e) => patchAbsoftNested('topLevelBonus', 'code', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500">{t('name')}</label>
                  <Input
                    value={topLevelBonus.name || ''}
                    onChange={(e) => patchAbsoftNested('topLevelBonus', 'name', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500">{t('ozTopLevelBonusAlways')}</label>
                  <Switch
                    checked={!!topLevelBonus.alwaysApply}
                    onChange={(v) => patchAbsoftNested('topLevelBonus', 'alwaysApply', v)}
                  />
                </div>
              </div>
            </div>
            {/* absoft.topLevelOtherIncome — other-income (206) kod override. */}
            <div>
              <div className="text-xs font-semibold text-gray-600 mb-1">{t('ozTopLevelOtherIncomeTitle')}</div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-gray-500">{t('code')}</label>
                  <Input
                    value={topLevelOtherIncome.code || ''}
                    onChange={(e) => patchAbsoftNested('topLevelOtherIncome', 'code', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500">{t('name')}</label>
                  <Input
                    value={topLevelOtherIncome.name || ''}
                    onChange={(e) => patchAbsoftNested('topLevelOtherIncome', 'name', e.target.value)}
                  />
                </div>
              </div>
            </div>
            {/* absoft.nmbVoucherReroute — NMB vaučer reroute na legacy/special-leave putanji (P3: 217→212). */}
            <div>
              <div className="text-xs font-semibold text-gray-600 mb-1">{t('ozNmbVoucherRerouteTitle')}</div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-gray-500">{t('code')}</label>
                  <Input
                    value={nmbVoucherReroute.code || ''}
                    onChange={(e) => patchAbsoftNested('nmbVoucherReroute', 'code', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500">{t('name')}</label>
                  <Input
                    value={nmbVoucherReroute.name || ''}
                    onChange={(e) => patchAbsoftNested('nmbVoucherReroute', 'name', e.target.value)}
                  />
                </div>
              </div>
            </div>
            {/* absoft.nonCashContribPriorityStatuses — statusi kojima nenovčana (N01) komponenta dobija prioritet u capovanoj osnovici doprinosa (Paysend 029). */}
            <div>
              <div className="text-xs font-semibold text-gray-600 mb-1">{t('ozNonCashPriorityTitle')}</div>
              <Select
                mode="multiple"
                allowClear
                className="w-full"
                placeholder="029"
                value={config?.absoft?.nonCashContribPriorityStatuses || []}
                options={STATUS_OPTIONS.map((s) => ({ label: s, value: s }))}
                onChange={(v) => patchAbsoft('nonCashContribPriorityStatuses', v)}
              />
              <div className="text-xs text-gray-400 mt-1">
                {t('ozNonCashPriorityHint')}
              </div>
            </div>
          </div>
        ),
      },
      {
        key: 'variationMappings',
        label: t('ozPanelVariationMappings'),
        children: (
          <div>
            <VariationMappingTable
              title={t('ozVariationBonus')}
              items={config?.variationMappings?.bonus || []}
              onChange={(list) => patchVar('bonus', list)}
              hint={t('ozVariationHintBonus')}
            />
            <VariationMappingTable
              title={t('ozVariationNmb')}
              items={config?.variationMappings?.nmb || []}
              onChange={(list) => patchVar('nmb', list)}
            />
            <VariationMappingTable
              title={t('ozVariationSuspension')}
              items={config?.variationMappings?.suspension || []}
              onChange={(list) => patchVar('suspension', list)}
              hint={`${t('ozVariationHintSuspension')} ${t('ozVariationItemizedHint')}`}
              showItemized
            />
          </div>
        ),
      },
      {
        key: 'dopunaConfig',
        label: t('ozPanelDopuna'),
        children: (
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="block text-xs text-gray-500">
                {t('ozDopunaEnabled')}
              </label>
              <Switch
                checked={dopuna.enabled !== false}
                onChange={(v) => patchNested('dopunaConfig', 'enabled', v)}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500">
                {t('ozDopunaMinGap')}
              </label>
              <InputNumber
                className="w-full"
                value={dopuna.minGap ?? 5000}
                onChange={(v) => patchNested('dopunaConfig', 'minGap', v)}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500">
                {t('ozDopunaFillCode')}
              </label>
              <Input
                value={dopuna.fillEarningCode || '213'}
                onChange={(e) =>
                  patchNested('dopunaConfig', 'fillEarningCode', e.target.value)
                }
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500">
                {t('ozDopunaFillName')}
              </label>
              <Input
                value={dopuna.fillEarningName || ''}
                onChange={(e) =>
                  patchNested('dopunaConfig', 'fillEarningName', e.target.value)
                }
              />
            </div>
          </div>
        ),
      },
      {
        key: 'flags',
        label: t('ozPanelCalcFlags'),
        children: (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500">
                {t('ozFlagsAbsoftTip40')}
              </label>
              <Switch
                checked={!!config?.useAbsoftTip40Formula}
                onChange={(v) => patch('useAbsoftTip40Formula', v)}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500">
                {t('ozFlagsTip40Multiplier')}
              </label>
              <Switch
                checked={!!config?.absoft?.useAbsoftTip40Multiplier}
                onChange={(v) => patchAbsoft('useAbsoftTip40Multiplier', v)}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500">
                {t('ozFlagsType02CalendarFond')}
              </label>
              <Switch
                checked={!!config?.absoft?.useType02CalendarFond}
                onChange={(v) => patchAbsoft('useType02CalendarFond', v)}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500">
                {t('ozFlagsPerEmpNetCoef')}
              </label>
              <Switch
                checked={!!config?.usePerEmployeeNetCoef}
                onChange={(v) => patch('usePerEmployeeNetCoef', v)}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500">
                {t('ozFlagsNightOvertime')}
              </label>
              <Switch
                checked={!!config?.nightWorkAsOvertime}
                onChange={(v) => patch('nightWorkAsOvertime', v)}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500">
                {t('ozFlagsEmpCoefOverride')}
              </label>
              <InputNumber
                className="w-full"
                step={0.001}
                value={config?.empCoefOverride}
                onChange={(v) => patch('empCoefOverride', v)}
              />
            </div>
          </div>
        ),
      },
      {
        key: 'absoftAdvancedFlags',
        label: 'AB Soft napredni flagovi (per-firma)',
        children: (
          <div>
            <div className="mb-2 text-xs text-gray-500">
              Per-firma AB Soft podešavanja koja menjaju obračun. Svaki flag je gated — podrazumevano isključen = nepromenjeno ponašanje.
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              {ABSOFT_BOOL_FLAGS.map((f) => (
                <div key={f.key} className="flex items-start justify-between gap-2 border-b border-gray-100 py-1">
                  <div className="min-w-0">
                    <div className="text-xs font-medium">{f.label}</div>
                    <div className="truncate text-[11px] text-gray-400" title={f.hint}>{f.hint}</div>
                  </div>
                  <Switch
                    size="small"
                    checked={config?.absoft?.[f.key] === true}
                    onChange={(v) => patchAbsoft(f.key, v)}
                  />
                </div>
              ))}
            </div>
            <div className="mt-3 grid grid-cols-4 gap-3">
              <div>
                <label className="block text-xs text-gray-500">useTaxGroupsFrom (YYYYMM)</label>
                <Input
                  size="small"
                  placeholder="202605"
                  value={config?.absoft?.useTaxGroupsFrom || ''}
                  onChange={(e) => patchAbsoft('useTaxGroupsFrom', e.target.value || undefined)}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500">Šifra dugog bolovanja 30+</label>
                <Input
                  size="small"
                  placeholder="122"
                  value={config?.absoft?.longSickEarningCode || ''}
                  onChange={(e) => patchAbsoft('longSickEarningCode', e.target.value || undefined)}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500">DPF mesečni limit (RSD)</label>
                <InputNumber
                  size="small"
                  className="w-full"
                  min={0}
                  placeholder="8677"
                  value={config?.absoft?.dpfMonthlyLimit ?? null}
                  onChange={(v) => patchAbsoft('dpfMonthlyLimit', v ?? undefined)}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500">Šifra obustave za nenovčana davanja</label>
                <Input
                  size="small"
                  placeholder="P34"
                  value={config?.absoft?.nonCashDeductionCode || ''}
                  onChange={(e) => patchAbsoft('nonCashDeductionCode', e.target.value || undefined)}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500">minBR (bruto satnica TYPE_62)</label>
                <InputNumber
                  size="small"
                  className="w-full"
                  min={0}
                  value={config?.absoft?.minBR ?? null}
                  onChange={(v) => patchAbsoft('minBR', v ?? undefined)}
                />
              </div>
            </div>
            <div className="mt-3">
              <ContributionStatusOverridesTable
                value={config?.absoft?.contributionStatusOverrides}
                onChange={(obj) => patchAbsoft('contributionStatusOverrides', obj)}
              />
            </div>
          </div>
        ),
      },
      {
        key: 'excludeFromAverage',
        label: t('ozPanelExcludeFromAverage'),
        children: (
          <Select
            mode="tags"
            className="w-full"
            value={config?.excludeFromAverage || []}
            tokenSeparators={[',', ' ']}
            placeholder={t('ozExcludeFromAveragePlaceholder')}
            onChange={(v) => patch('excludeFromAverage', v)}
          />
        ),
      },
      {
        key: 'absoftCore',
        label: t('ozPanelAbsoftCore'),
        children: (
          <div>
            <div className="mb-3 grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-500">
                  {t('ozAbsoftUseCorrection')}
                </label>
                <Switch
                  checked={config?.absoft?.useCorrection === true}
                  onChange={(v) => patchAbsoft('useCorrection', v)}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500">
                  {t('ozAbsoftNetoBr')}
                </label>
                <Select
                  className="w-full"
                  value={config?.absoft?.netoBr ?? 2}
                  options={[
                    { value: 1, label: t('ozAbsoftNetoBr1') },
                    { value: 2, label: t('ozAbsoftNetoBr2') },
                  ]}
                  onChange={(v) => patchAbsoft('netoBr', v)}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500">
                  {t('ozAbsoftNaknObr41')}
                </label>
                <Select
                  className="w-full"
                  value={config?.absoft?.naknOBR41 ?? 12}
                  options={[
                    { value: 3, label: t('ozAbsoftNakn3M') },
                    { value: 12, label: t('ozAbsoftNakn12M') },
                  ]}
                  onChange={(v) => patchAbsoft('naknOBR41', v)}
                />
              </div>
            </div>
            <Card size="small" title={t('ozAbsoftBugCompat')} className="mb-3">
              <div>
                <label className="block text-xs text-gray-500">
                  {t('ozAbsoftClassify151')}
                </label>
                <Switch
                  checked={
                    config?.absoft?.bugCompatibility?.classifyOtOnHolidayAs151 ===
                    true
                  }
                  onChange={(v) =>
                    patchBugCompat('classifyOtOnHolidayAs151', v)
                  }
                />
              </div>
            </Card>
            <Ia31MonthEditor
              ia31={config?.absoft?.ia31}
              onChange={(v) => patchAbsoft('ia31', v)}
            />
          </div>
        ),
      },
      {
        key: 'statusMatrixOverrides',
        label: t('ozPanelStatusMatrixOverrides'),
        children: (
          <StatusMatrixOverridesTable
            items={config?.statusMatrixOverrides || []}
            onChange={(list) => patch('statusMatrixOverrides', list)}
          />
        ),
      },
      {
        key: 'earningCodeBuckets',
        label: t('ozPanelEarningBuckets'),
        children: (
          <EarningCodeBucketsTable
            items={config?.absoft?.earningCodeBuckets || []}
            onChange={(list) => patchAbsoft('earningCodeBuckets', list)}
          />
        ),
      },
      {
        key: 'earningCodeOverrides',
        label: t('ozPanelEarningCodeOverrides'),
        children: (
          <EarningCodeOverridesTable
            items={config?.earningCodeOverrides || []}
            onChange={(list) => patch('earningCodeOverrides', list)}
          />
        ),
      },
      {
        key: 'workTypeOverrides',
        label: t('ozPanelWorkTypeOverrides'),
        children: (
          <WorkTypeOverridesTable
            items={config?.workTypeOverrides || []}
            onChange={(list) => patch('workTypeOverrides', list)}
          />
        ),
      },
      {
        key: 'sectorRules',
        label: t('ozPanelSectorRules'),
        children: (
          <SectorRulesTable
            items={config?.sectorRules || []}
            onChange={(list) => patch('sectorRules', list)}
          />
        ),
      },
      {
        key: 'skipCorrectionEarningCodes',
        label: t('ozPanelSkipCorrectionCodes'),
        children: (
          <div>
            <div className="mb-2 text-xs text-gray-500">
              {t('ozSkipCorrectionHint')}
            </div>
            <Select
              mode="tags"
              className="w-full"
              value={config?.skipCorrectionEarningCodes || []}
              tokenSeparators={[',', ' ']}
              placeholder={t('ozSkipCorrectionPlaceholder')}
              onChange={(v) => patch('skipCorrectionEarningCodes', v)}
            />
          </div>
        ),
      },
    ]} />
  );
}
